import React, { useState, useCallback } from 'react';
import { Upload, X, AlertCircle, ExternalLink } from 'lucide-react';
import { uploadApi } from '../../lib/api';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

export const ImageUpload = ({ onUpload, currentImage, className }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(currentImage || null);
    const [uploadedFileName, setUploadedFileName] = useState(null);

    const validateImage = (file) => {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => {
                if (img.width !== 500 || img.height !== 500) {
                    reject(new Error(`Bild muss exakt 500x500 Pixel sein. Dein Bild ist ${img.width}x${img.height}.`));
                } else {
                    resolve(true);
                }
            };
            img.onerror = () => reject(new Error('Ungültige Bilddatei'));
            img.src = URL.createObjectURL(file);
        });
    };

    const handleFile = useCallback(async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            setError('Bitte wähle eine Bilddatei aus');
            return;
        }
        setError(null);
        setIsUploading(true);
        try {
            await validateImage(file);
            const response = await uploadApi.uploadImage(file);
            // URL is now complete from backend (https://assets.armesa.de/img/...)
            const imageUrl = response.data.url;
            setPreview(imageUrl);
            setUploadedFileName(response.data.original_filename || file.name);
            onUpload(imageUrl);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Upload fehlgeschlagen');
        } finally {
            setIsUploading(false);
        }
    }, [onUpload]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFile(e.dataTransfer.files[0]);
    }, [handleFile]);

    const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
    const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
    const handleFileSelect = useCallback((e) => handleFile(e.target.files[0]), [handleFile]);
    const handleRemove = useCallback(() => { setPreview(null); setError(null); setUploadedFileName(null); onUpload(null); }, [onUpload]);

    return (
        <div className={cn("space-y-3", className)}>
            {preview ? (
                <div className="space-y-2">
                    <div className="relative group">
                        <img src={preview} alt="Preview" className="w-full h-48 object-cover rounded-lg border border-white/10" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                            <Button type="button" variant="destructive" size="sm" onClick={handleRemove} className="bg-red-500 hover:bg-red-400">
                                <X size={16} className="mr-1" /> Entfernen
                            </Button>
                        </div>
                    </div>
                    {/* Display uploaded filename as link */}
                    {uploadedFileName && (
                        <div className="flex items-center gap-2 text-sm">
                            <ExternalLink size={14} className="text-cyan-400" />
                            <a 
                                href={preview} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-cyan-400 hover:text-cyan-300 hover:underline truncate"
                            >
                                {uploadedFileName}
                            </a>
                        </div>
                    )}
                </div>
            ) : (
                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={cn(
                        "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                        isDragging ? "border-cyan-500 bg-cyan-500/10" : "border-white/20 hover:border-white/40 bg-black/20",
                        isUploading && "pointer-events-none opacity-50"
                    )}
                >
                    <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" id="image-upload" disabled={isUploading} />
                    <label htmlFor="image-upload" className="cursor-pointer block">
                        <div className="flex flex-col items-center gap-3">
                            {isUploading ? (
                                <div className="w-12 h-12 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                                    <Upload size={24} className="text-gray-400" />
                                </div>
                            )}
                            <div>
                                <p className="text-sm text-gray-300">{isUploading ? 'Wird hochgeladen...' : 'Klicken oder Bild hierher ziehen'}</p>
                                <p className="text-xs text-gray-500 mt-1">Exakt 500x500 Pixel erforderlich</p>
                            </div>
                        </div>
                    </label>
                </div>
            )}
            {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    <AlertCircle size={16} /><span>{error}</span>
                </div>
            )}
        </div>
    );
};
